import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {Button, SearchSelect, themeStore, userStore, exchangesStore, typeOptions, PlotGraphMenuProps, TFilterOption, TMarket, TPair, TType} from "@root/common"
import { PlotGraphMenuStyled } from "./PlotGraphMenu.styled"
import { observer } from "mobx-react-lite"
import { getMarkets, getPairs } from "../../api/filters.api"
import plotmenuStore from "../../store/plotmenu.store"


const PlotGraphMenu: FC<PlotGraphMenuProps> = observer(({onCreate, showBtn}) => {
    const plotMenuRef = useRef(null)

    const [type, setType] = useState<TFilterOption<TType>>()
    const [coin, setCoin] = useState<TFilterOption<string | undefined> | undefined>(undefined)
    const [long, setLong] = useState<TFilterOption<string | undefined> | undefined>(undefined)
    const [short, setShort] = useState<TFilterOption<string | undefined> | undefined>(undefined)

    const [isDefault, setIsDefault] = useState({
        type: true,
        coin: true,
        long: true,
        short: true
    })

    useEffect(() => {
        if (plotmenuStore.defaultType) {
            const type = plotmenuStore.defaultType
            setType({option: type, name: typeOptions.find(opt => opt.option === type)?.name || ''})
            localStorage.setItem('type1', type)
            localStorage.setItem('type2', type)
        }
    }, [plotmenuStore.defaultType])

    const [longMarkets, setLongMarkets] = useState<Record<string, TMarket[]>>({})
    const [shortMarkets, setShortMarkets] = useState<Record<string, TMarket[]>>({})
    const [pairs, setPairs] = useState<TPair[]>([])

    useEffect(() => {
      if (!userStore.isAuth || !type) {
        return;
      }
      if (isDefault.type && type.option !== plotmenuStore.defaultType) {
          return;
      }

      const cb = () => {
          Promise.all([
              getPairs(type.option),
              getMarkets(type.option),
              getMarkets('futures'),
          ]).then(([pairs, longs, shorts]) => {
              localStorage.setItem('type2', type.option)
              setPairs(pairs?.sort((a,b) => parseFloat(b.funding) - parseFloat(a.funding)))
              if (type.option !== 'margin') {
                  setLongMarkets(longs)
                  setShortMarkets(shorts)
              } else {
                  setLongMarkets(shorts)
                  setShortMarkets(longs)

              }

          })
          setTimeout(() => localStorage.setItem('type1', type.option), 10000)
      }

      cb()
      const interval = setInterval(cb, 31000)

      return () => clearInterval(interval)
    }, [type, userStore.isAuth])

    const getLongShortOptions = useCallback((longShort: Record<string, TMarket[]>): TFilterOption[] => {
        if (exchangesStore.data && coin && longShort) {
            return exchangesStore.data
            ?.filter(ex => longShort[coin.option || '']?.map(ls => ls.exchange)?.includes(ex.market))
            ?.map(ex => ({option: ex.market, name: ex.name, interval: longShort[coin.option || ''].find(ls => ls.exchange === ex.market)?.interval,  funding: longShort[coin.option || ''].find(ls => ls.exchange === ex.market)?.funding}))
        }

        return []
    }, [exchangesStore.data, coin])

    const marketOptions: TFilterOption[] = useMemo(() => {
        return pairs.map(pair => ({option: pair.pair, name: pair.pair, funding: pair.funding}))
    }, [pairs])

    const longOptions = useMemo(() => {
        return getLongShortOptions(longMarkets).sort((a,b) => parseFloat(a.funding || '0') - parseFloat(b.funding || '0'))
    }, [getLongShortOptions, longMarkets])

    const shortOptions = useMemo(() => {
        return getLongShortOptions(shortMarkets).sort((a,b) => parseFloat(b.funding || '0') - parseFloat(a.funding || '0'))
    }, [getLongShortOptions, shortMarkets])

    useEffect(() => {
        if (plotmenuStore.defaultCoin && isDefault.coin) {
            setCoin({option: plotmenuStore.defaultCoin, name: plotmenuStore.defaultCoin, funding: pairs.find(pair => pair.pair === plotmenuStore.defaultCoin)?.funding})
        }
    }, [plotmenuStore.defaultCoin, pairs])

    useEffect(() => {
        if(plotmenuStore.defaultLong && isDefault.long) {
            setLong(longOptions.find(opt => opt.option === plotmenuStore.defaultLong))
        }
    }, [plotmenuStore.defaultLong, longOptions])

    useEffect(() => {
        if(plotmenuStore.defaultShort && isDefault.short) {
            setShort(shortOptions.find(opt => opt.option === plotmenuStore.defaultShort))
        }
    }, [plotmenuStore.defaultShort, shortOptions])


    useEffect(() => {
        const type1 = localStorage.getItem('type1')
        const type2 = localStorage.getItem('type2')
        if ((!isDefault.coin || plotmenuStore.defaultCoin === '')) {
            const pair = pairs[0]
            const currentCoin = pairs.find(item => item.pair === coin?.name)
            setCoin(type1 !== type2 ? {name: pair?.pair, option: pair?.pair, funding: pair?.funding} : {name: coin?.name || '', option: coin?.name || '', funding: currentCoin?.funding })
        }
    }, [pairs, plotmenuStore.defaultCoin])

    useEffect(() => {
        if (!isDefault.long || plotmenuStore.defaultLong === '') {
            const long = longOptions[0]
            setLong(long)
        }
    }, [longOptions, plotmenuStore.defaultLong])

    useEffect(() => {
        if (!isDefault.short || plotmenuStore.defaultShort === '') {
            const short = shortOptions[0]
            setShort(short)
        }
    }, [shortOptions, plotmenuStore.defaultShort])

    useEffect(() => {
        window.addEventListener('keydown', e => {
            if (e.code === 'Enter') {
                //@ts-ignore
                plotMenuRef.current.children[4]?.click()
            }
        })
    }, [])


    return (
        <PlotGraphMenuStyled
            style={{backgroundColor: themeStore.bgColor, borderColor: themeStore.textColor}}
            ref={plotMenuRef}
        >
            <SearchSelect
                className="select"
                title={"Тип"}
                choice={type}
                setChoice={val => {
                    localStorage.setItem('type1', type?.option || '')
                    setType(val)
                    if (isDefault.type) {
                        setIsDefault({...isDefault, type: false, coin: false, long: false, short: false})
                    }

                }}
                options={typeOptions}
            />
            <SearchSelect
                className="select"
                title={"Пара"}
                choice={coin}
                setChoice={val => {
                    setCoin(val)
                    if (isDefault.coin) {
                        setIsDefault({...isDefault, coin: false, long: false, short: false})
                    }
                }}
                options={marketOptions}
            />
            <SearchSelect
                className="select"
                title={"Лонг"}
                choice={long}
                setChoice={val => {
                    setLong(val)
                    if (isDefault.long) {
                        setIsDefault({...isDefault, long: false})
                    }
                }}
                options={longOptions}
            />
            <SearchSelect
                className="select"
                title={"Шорт"}
                choice={short}
                setChoice={val => {
                    setShort(val)
                    if (isDefault.short) {
                        setIsDefault({...isDefault, short: false})
                    }
                }}
                options={shortOptions}
            />

            {showBtn &&
                <Button
                    onClick={() => {
                        if (type?.option && coin?.option && long?.option && short?.option) {
                            onCreate({
                                type: type.option,
                                coin: coin.option,
                                long: long.option,
                                short: short.option
                            })
                        }
                    }}
                >
                    <p>Создать</p>
                </Button>
            }
        </PlotGraphMenuStyled>
    )

})

export default PlotGraphMenu
